import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import ChatConversion from "units/chatbot/components/ChatConversion";
import ChatBox from "units/chatbot/components/ChatBox";
import { useConversations } from "units/chatbot/hooks/useConversations";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import UserSidebar from "units/user/components/UserSidebar";
import BotHeader from "units/chatbot/components/BotHeader";
import Header from "units/dashboard/components/Header";
import AdminSidebar from "units/admin/components/AdminSidebar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createAdminChatroom } from "../services/chatService";
import Footer from "units/dashboard/components/Footer";
import ClientChatBox from "units/client/ClientChatBot/ClientChatBox";
import { useTranslation } from "react-i18next";

const ChatBotContainerInner = ({ headerTitle }) => {
  const { t } = useTranslation(["user"]);

  const [chatHeight, setChatHeight] = useState("100vh");
  const [disable, setDisable] = useState(true);
  const [chatroomId, setChatroomId] = useState();
  const [isChatBotVisible, setIsChatBotVisible] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const user = useSelector((state) => state.userInfo);
  const userType = user?.userType;
  const userId = user?.userId;
  const { botType } = useParams();
  const selectedMenu = userType == 1 ? "supportBot" : "adminSupportBot";
  const { conversations, error, loadConversations, loading } = useConversations(botType);
  const menuVersion = useSelector((state) => state.menu.version);
  const accessToken = useSelector((state) => state.auth.accessToken);
  const navigate = useNavigate();
  const location = useLocation();
  const { navigatedFromWobList, chatroomIdd } = location.state || {};
  const { isSidebarOpen, toggleSidebar } = useSidebar();

  /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) {
        toggleSidebar();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  /* ---------- محاسبه ارتفاع چت ---------- */
  useEffect(() => {
    if (navigatedFromWobList === true && chatroomIdd) {
      setSelectedConversation({
        id: chatroomIdd,
        withGPT: true,
      });
      setIsChatBotVisible(false);
    }

    const elements = [
      document.getElementById("main-header"),
      document.getElementById("bot-header"),
      document.getElementById("chat-input"),
      document.getElementById("main-footer"),
    ];

    const calculate = () => {
      const total = elements.reduce(
        (sum, el) => sum + (el?.offsetHeight || 0),
        0
      );
      setChatHeight(`calc(100vh - ${total}px)`);
    };

    calculate();
    const observer = new ResizeObserver(calculate);
    elements.forEach((el) => el && observer.observe(el));
    window.addEventListener("resize", calculate);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", calculate);
    };
  }, [navigatedFromWobList, chatroomIdd]);

  useEffect(() => {
    if (navigatedFromWobList === true) return;
    setIsChatBotVisible(true);
    setSelectedConversation(null);
  }, [botType, navigatedFromWobList]);

  useEffect(() => {
    if (navigatedFromWobList === true) return;
    const fetchAndShowConversations = async () => {
      await loadConversations(selectedMenu);
      setIsChatBotVisible(true);
      setSelectedConversation(null);
    };
    fetchAndShowConversations();
  }, [selectedMenu, menuVersion, botType, navigatedFromWobList]);

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    setIsChatBotVisible(false);
  };

  const toggleView = async () => {
    try {
      if (isChatBotVisible) {
        setSelectedConversation(null);
        setIsChatBotVisible(false);
        const newChatroom = await createAdminChatroom(accessToken);
        if (newChatroom.ok && newChatroom.data?.id) {
          setSelectedConversation(newChatroom?.data);
          setIsChatBotVisible(false);
          setChatroomId(newChatroom?.data?.id);
        } else {
          console.error("خطا در ایجاد چت جدید:", newChatroom);
        }
      } else {
        setIsChatBotVisible(true);
        loadConversations(selectedMenu);
      }
    } catch (error) {
      console.error("خطا در ایجاد چت جدید:", error);
      if (error.response?.status === 403) {
        navigate("/login");
      }
    }
  };

  const toggleChatBotVisibility = () => {
    setIsChatBotVisible(true);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:!from-slate-950 dark:!via-slate-900 dark:!to-slate-950">

      {/* ================= Header ================= */}
      <nav
        id="main-header"
        className="
          shrink-0 z-30 h-[70px]
          flex items-center
          border-b border-teal-500/20 dark:!border-teal-400/15
          bg-white/85 dark:!bg-slate-900/85
          backdrop-blur-xl
          shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)]
          dark:!shadow-[0_4px_30px_-12px_rgba(0,0,0,0.7)]
          print:hidden
        "
      >
        <Header />
      </nav>

      {/* ================= Body ================= */}
      {/* min-h-0 اینجا حیاتیه: این div فرزند فلکس عمودیه، بدون min-h-0 اجازه نمی‌ده فرزندانش محدود بشن */}
      <div className="flex-1 min-h-0 relative flex flex-col">

        {/* Mobile backdrop */}
        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="
              lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30
              bg-slate-900/50 dark:!bg-black/60
              backdrop-blur-sm
              transition-opacity duration-300
            "
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed top-[70px] bottom-0
            rtl:right-0 ltr:left-0
            w-64
            z-40

            transform transition-transform duration-300 ease-out
            ${isSidebarOpen
              ? "translate-x-0"
              : "rtl:translate-x-full ltr:-translate-x-full"
            }
            lg:!translate-x-0

            rtl:border-l ltr:border-r
            border-teal-500/10 dark:!border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:!from-slate-900/95 dark:!via-slate-900/98 dark:!to-slate-900/95
            backdrop-blur-xl
            shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden
            overflow-hidden
          `}
        >
          {userType == 1 ? (
            <UserSidebar
              isChatBotVisible={isChatBotVisible}
              toggleChatBotVisibility={toggleChatBotVisibility}
            />
          ) : (
            <AdminSidebar
              isChatBotVisible={isChatBotVisible}
              toggleChatBotVisibility={toggleChatBotVisibility}
            />
          )}
        </aside>

        {/* Main content — این کل ستون سمت راست/چپ (بدون سایدبار) رو کنترل می‌کنه */}
        <main
          className="
            flex-1 min-h-0 flex flex-col
            lg:rtl:pr-64 lg:ltr:pl-64 overflow-x-hidden
          "
        >
          {/* این div تنها بخشیه که باید اسکرول کنه */}
          <div
            className="
    flex-1 min-h-0 overflow-y-auto overflow-x-hidden
    scroll-smooth
    /* ============ Webkit (Chrome, Safari, Edge) ============ */
    [&::-webkit-scrollbar]:w-2.5
    [&::-webkit-scrollbar]:h-2.5
    [&::-webkit-scrollbar-track]:bg-transparent
    [&::-webkit-scrollbar-track]:rounded-full
    [&::-webkit-scrollbar-thumb]:rounded-full
    [&::-webkit-scrollbar-thumb]:bg-gradient-to-b
    [&::-webkit-scrollbar-thumb]:from-teal-500/40
    [&::-webkit-scrollbar-thumb]:via-teal-400/60
    [&::-webkit-scrollbar-thumb]:to-amber-400/50
    [&::-webkit-scrollbar-thumb]:border-2
    [&::-webkit-scrollbar-thumb]:border-transparent
    [&::-webkit-scrollbar-thumb]:bg-clip-padding
    hover:[&::-webkit-scrollbar-thumb]:from-teal-500/70
    hover:[&::-webkit-scrollbar-thumb]:via-teal-400/90
    hover:[&::-webkit-scrollbar-thumb]:to-amber-400/80
    [&::-webkit-scrollbar-thumb]:transition-colors
    [&::-webkit-scrollbar-thumb]:duration-300
    [&::-webkit-scrollbar-corner]:bg-transparent

    /* ============ Firefox ============ */
    [scrollbar-width:thin]
    [scrollbar-color:rgba(20,184,166,0.5)_transparent]
    dark:[scrollbar-color:rgba(251,191,36,0.4)_transparent]
  "
          >
            <div className="page-content pt-0">
              <div className="container-fluid px-3 sm:px-4 md:px-6">

                {/* Breadcrumb spacer */}
                <div className="grid grid-cols-1 mb-4">
                  <div className="flex items-center justify-between">
                    <nav className="flex" aria-label={t("chatBot.breadcrumb")}></nav>
                  </div>
                </div>

                {/* ============ Chat Card ============ */}
                <div
                  className="
                    relative rounded-2xl
                    bg-white/70 dark:!bg-slate-900/70
                    backdrop-blur-xl
                    border border-teal-500/15 dark:!border-teal-400/15
                    shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)]
                    dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
                    p-3 sm:p-4
                    transition-colors duration-500
                    min-w-0
                  "
                >
                  {/* تزئینات داخل wrapper اختصاصی */}
                  <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
                    <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-300/10 blur-3xl dark:!bg-amber-400/5" />
                    <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-teal-400/10 blur-3xl dark:!bg-teal-500/5" />
                  </div>

                  <div className="relative">
                    {/* BotHeader */}
                    <BotHeader
                      headerTitle={
                        botType === "RayaBot"
                          ? t("chatBot.customerConversations")
                          : t("chatBot.supportConversations")
                      }
                      isChatBotVisible={isChatBotVisible}
                      toggleView={toggleView}
                      accessToken={accessToken}
                      botType={botType}
                    />

                    {/* Error */}
                    {error ? (
                      <div className="mt-4 rounded-xl border border-rose-400/40 bg-rose-500/10 text-rose-600 dark:!text-rose-300 p-4 text-sm">
                        {error}
                      </div>
                    ) : isChatBotVisible ? (
                      <ChatConversion
                        loading={loading}
                        selectedItem={botType}
                        title={headerTitle}
                        conversations={conversations}
                        onConversationClick={handleConversationClick}
                        selectedMenu={selectedMenu}
                      />
                    ) : selectedConversation?.withGPT ? (
                      <ClientChatBox
                        toggleView={toggleView}
                        height={chatHeight}
                        selectedConversation={selectedConversation}
                        withGPT={selectedConversation?.withGPT}
                        accessToken={accessToken}
                        username={user?.username}
                        name={user?.name}
                        disable={disable}
                        partnerId={selectedConversation?.sender?.phone}
                        phone={selectedConversation?.sender?.phone}
                        countryCode={selectedConversation?.sender?.countryCode}
                      />
                    ) : (
                      <ChatBox
                        selectedConversation={selectedConversation}
                        partnerId={selectedConversation?.receiverId}
                        chatroomId={selectedConversation?.id}
                        withGPT={selectedConversation?.withGPT}
                        botType={botType}
                        accessToken={accessToken}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Footer />
          </div>
        </main>
      </div>
    </div>
  );
};

const ChatBotContainer = ({ headerTitle }) => (
  <SidebarProvider>
    <ChatBotContainerInner headerTitle={headerTitle} />
  </SidebarProvider>
);

export default ChatBotContainer;